/* eslint-disable no-param-reassign */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import SignaturePad from 'signature_pad';
import { drawLine } from './SignatureText';

const minWidth = 0.5;
const maxWidth = 2;

const SignatureDraw = React.forwardRef((props, ref) => {
  const { displayWidth, displayHeight, defaultValue, actualWidth, actualHeight, color, showLine, readOnly, onSignatureChange } = props;
  const signaturePadRef = useRef();
  const canvasRef = useRef();
  const lineCanvasRef = useRef();
  const [hasChanged, setHasChanged] = useState(false);
  const scale = displayWidth / actualWidth;

  useEffect(() => {
    const signaturePad = new SignaturePad(canvasRef.current);

    signaturePad.addEventListener('beginStroke', () => {
      setHasChanged(true);
    });

    signaturePad.addEventListener('endStroke', () => {
      onSignatureChange();
    })

    signaturePadRef.current = signaturePad;

    if (readOnly) {
      //SignaturePad library does not have an option for readOnly, but the Off event has the same effect
      signaturePad.off();
    }

    return () => signaturePad.off();
  }, []);

  useEffect(() => {
    signaturePadRef.current.penColor = color;
    signaturePadRef.current.minWidth = minWidth * scale;
    signaturePadRef.current.maxWidth = maxWidth * scale;

    //update the default value if its getting updated from outside this component
    if (!hasChanged && defaultValue) {
      try {
        signaturePadRef.current.fromData(scaleData(defaultValue, scale));
      } catch (err) {
        console.error(err);
      }
    }
  }, [color, defaultValue, scale]);

  // persist data across canvas resizing
  const previousData = useRef();
  useEffect(() => {
    signaturePadRef.current.minWidth = minWidth * scale;
    signaturePadRef.current.maxWidth = maxWidth * scale;

    if (showLine) {
      drawLine(lineCanvasRef.current, displayWidth, displayHeight);
    }

    if (previousData.current) {
      // render data with current scale
      signaturePadRef.current.fromData(scaleData(previousData.current, scale));
    }
    return () => {
      // save off the data with normal scale
      previousData.current = scaleData(signaturePadRef.current.toData(), 1 / scale);
    };
  }, [displayWidth, displayHeight, scale, showLine]);

  ref.current = {
    toDataURL: () => {
      const imageCanvas = document.createElement('canvas');
      imageCanvas.width = actualWidth;
      imageCanvas.height = actualHeight;
      const imageSignaturePad = new SignaturePad(imageCanvas, {
        minWidth,
        maxWidth,
        penColor: color
      });
      imageSignaturePad.fromData(scaleData(signaturePadRef.current.toData(), 1 / scale));
      return imageCanvas.toDataURL();
    },
    toData: () => {
      return scaleData(signaturePadRef.current.toData(), 1 / scale);
    },
    hasChanged: () => hasChanged,
    clear: () => {
      signaturePadRef.current.clear();
      setHasChanged(true);
    }
  };

  return (
    <>
      {showLine ? <canvas ref={lineCanvasRef} width={displayWidth} height={displayHeight} style={{ position: 'absolute' }} /> : null}
      <canvas
        ref={canvasRef}
        width={displayWidth}
        height={displayHeight}
        style={{ position: 'relative' }}
        onMouseDown={onSignatureChange}
        onTouchStart={onSignatureChange}
      />
    </>
  );
});

SignatureDraw.propTypes = {
  displayWidth: PropTypes.number,
  displayHeight: PropTypes.number,
  actualWidth: PropTypes.number,
  actualHeight: PropTypes.number,
  color: PropTypes.string,
  defaultValue: PropTypes.array
};

export default SignatureDraw;

function scaleData(data, scale) {
  const newData = [];
  for (const line of data) {
    const newLine = {
      color: line.color,
      points: []
    };
    if (line.points) {
      for (const point of line.points) {
        newLine.points.push({
          time: point.time,
          x: point.x * scale,
          y: point.y * scale
        });
      }
    }
    newData.push(newLine);
  }
  return newData;
}
